/**
 * NotificationsSection Component
 * Categorized notification preferences matrix
 */

import UpgradeModal from '@components/ui/UpgradeModal';
import type { User, UserPreferences } from '@contexts/AuthContext';
import { useWebPush, WEB_PUSH_FAILURE, type WebPushFailure } from '@hooks/useWebPush';
import type { GatedFeature } from '@parlay-picker/shared/featureAccess';
import {
  canChooseSlateAlertBooks,
  DEFAULT_NOTIFICATION_PREFERENCES,
  groupNotificationSubGroups,
  LOCKED_NOTIFICATION_TYPES,
  NOTIFICATION_SUB_GROUPS,
  type NotificationPreferences,
  type NotificationSubGroup,
  type SubGroupInfo,
  subGroupFeature,
} from '@parlay-picker/shared/notification';
import {
  getBookDisplayName,
  getSlateAlertBooks,
  type OddsApiSportsbookId,
  SPORTSBOOKS_ODDS_API,
} from '@parlay-picker/shared/sportsbook';
import type { SubscriptionTier } from '@parlay-picker/shared/subscriptionTier';
import { ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { type ReactElement, useMemo, useState } from 'react';

interface NotificationsSectionProps {
  user: User | null;
  onUpdatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
}

const CATEGORY_LABELS: Record<SubGroupInfo['category'], string> = {
  settlement: 'Settlement',
  liveGame: 'Live Game',
  social: 'Social',
  account: 'Account',
  system: 'System',
  monitoring: 'Monitoring',
};

const CATEGORY_ORDER: SubGroupInfo['category'][] = [
  'settlement',
  'liveGame',
  'social',
  'account',
  'system',
  'monitoring',
];

const CHANNEL_LABELS = [
  { key: 'inApp' as const, label: 'In-App' },
  { key: 'email' as const, label: 'Email' },
  { key: 'webPush' as const, label: 'Push' },
];

/**
 * Computes which sub-groups contain locked notification types
 * (types in LOCKED_NOTIFICATION_TYPES that cannot be disabled).
 */
function getLockedSubGroups(): Set<NotificationSubGroup> {
  const lockedSubGroups = new Set<NotificationSubGroup>();
  for (const lockedType of LOCKED_NOTIFICATION_TYPES) {
    const subGroup = NOTIFICATION_SUB_GROUPS[lockedType];
    if (subGroup) lockedSubGroups.add(subGroup);
  }
  return lockedSubGroups;
}

const SECURITY_LOCKED_SUB_GROUPS = getLockedSubGroups();

function MiniToggle({
  enabled,
  label,
  onToggle,
  disabled = false,
  securityLocked = false,
}: {
  enabled: boolean;
  label: string;
  onToggle: () => void;
  disabled?: boolean;
  securityLocked?: boolean;
}): ReactElement {
  const isDisabled = disabled || securityLocked;
  const isForceEnabled = securityLocked;

  return (
    <button
      type="button"
      onClick={onToggle}
      title={
        securityLocked
          ? 'This notification cannot be disabled for your security'
          : `${label}: ${enabled ? 'On' : 'Off'}`
      }
      disabled={isDisabled}
      className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors ${
        isForceEnabled
          ? 'opacity-60 cursor-not-allowed bg-theme-primary/20 text-theme-primary border border-theme-primary/30'
          : disabled
            ? 'opacity-40 cursor-not-allowed bg-theme-surface-hover text-theme-text-tertiary border border-theme-border'
            : enabled
              ? 'bg-theme-primary/20 text-theme-primary border border-theme-primary/30'
              : 'bg-theme-surface-hover text-theme-text-tertiary border border-theme-border'
      }`}
    >
      <span
        className={`w-2 h-2 rounded-full transition-colors ${
          (enabled || isForceEnabled) && !disabled ? 'bg-theme-primary' : 'bg-theme-border'
        }`}
      />
      {label}
    </button>
  );
}

export function NotificationsSection({
  user,
  onUpdatePreferences,
}: NotificationsSectionProps): ReactElement {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(CATEGORY_ORDER),
  );
  const [upgradeFeature, setUpgradeFeature] = useState<GatedFeature | null>(null);

  const currentPrefs: NotificationPreferences =
    user?.preferences?.notifications ?? DEFAULT_NOTIFICATION_PREFERENCES;

  const userTier: SubscriptionTier = user?.role ?? 'free';

  const groupedByCategory = useMemo(() => groupNotificationSubGroups(userTier), [userTier]);

  const canChooseSlateBooks = canChooseSlateAlertBooks(userTier);
  const slateBooks = getSlateAlertBooks(user?.preferences);

  const toggleSlateBook = (book: OddsApiSportsbookId) => {
    const next = slateBooks.includes(book)
      ? slateBooks.filter((b) => b !== book)
      : [...slateBooks, book];
    onUpdatePreferences({ slateAlertBooks: next });
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Push is the account's setting, which the phone's pushes read too; turning it on also subscribes this browser
  const webPush = useWebPush();
  const [pushNotice, setPushNotice] = useState<string | null>(null);

  const handlePushToggle = async (subGroup: NotificationSubGroup) => {
    setPushNotice(null);
    const isTurningOn = !currentPrefs[subGroup].webPush;
    const subscribeResult = isTurningOn && !webPush.isSubscribed ? await webPush.subscribe() : null;
    const updated: NotificationPreferences = {
      ...currentPrefs,
      [subGroup]: { ...currentPrefs[subGroup], webPush: isTurningOn },
    };
    await onUpdatePreferences({ notifications: updated });
    if (subscribeResult && !subscribeResult.success) {
      const failureMessages: Record<WebPushFailure, string> = {
        [WEB_PUSH_FAILURE.DENIED]:
          "Push is on, but your browser blocks notifications from this site, so this browser won't get them. Allow them in your browser's site settings, then turn Push off and on again.",
        [WEB_PUSH_FAILURE.DISMISSED]:
          "Push is on, but this browser wasn't given permission, so it won't get them. Turn Push off and on again and choose Allow.",
        [WEB_PUSH_FAILURE.UNSUPPORTED]:
          "Push is on, but this browser can't receive push notifications.",
        [WEB_PUSH_FAILURE.NOT_CONFIGURED]:
          "Push is on, but push notifications aren't set up for this site yet, so this browser won't get them.",
        [WEB_PUSH_FAILURE.FAILED]:
          "Push is on, but this browser couldn't be subscribed. Turn Push off and on again to retry.",
      };
      setPushNotice(failureMessages[subscribeResult.reason]);
    }
    if (!isTurningOn && !Object.values(updated).some((toggles) => toggles.webPush)) {
      await webPush.unsubscribe();
    }
  };

  const handleChannelToggle = (
    subGroup: NotificationSubGroup,
    channel: 'inApp' | 'email' | 'webPush',
    locked: boolean,
  ) => {
    if (locked) {
      setUpgradeFeature(subGroupFeature(subGroup) ?? null);
      return;
    }
    if (channel === 'webPush') {
      handlePushToggle(subGroup);
      return;
    }
    const updated: NotificationPreferences = {
      ...currentPrefs,
      [subGroup]: {
        ...currentPrefs[subGroup],
        [channel]: !currentPrefs[subGroup][channel],
      },
    };
    onUpdatePreferences({ notifications: updated });
  };

  return (
    <section className="mb-6">
      <h3 className="text-lg font-medium text-theme-text mb-2">Notifications</h3>
      {pushNotice && (
        <p role="status" data-testid="push-notice" className="text-xs text-theme-warning mb-2">
          {pushNotice}
        </p>
      )}

      {/* Notification Preferences Matrix */}
      <div className="bg-theme-surface rounded-lg border border-theme-border overflow-hidden">
        {CATEGORY_ORDER.map((category) => {
          const subGroups = groupedByCategory.get(category);
          if (!subGroups || subGroups.length === 0) return null;

          const isExpanded = expandedCategories.has(category);

          return (
            <div key={category}>
              {/* Category header */}
              <button
                type="button"
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center justify-between px-4 py-3 border-b border-theme-border hover:bg-theme-surface-hover transition-colors"
              >
                <span className="text-xs font-semibold uppercase tracking-wider text-theme-text-secondary">
                  {CATEGORY_LABELS[category]}
                </span>
                {isExpanded ? (
                  <ChevronUp size={14} className="text-theme-text-tertiary" />
                ) : (
                  <ChevronDown size={14} className="text-theme-text-tertiary" />
                )}
              </button>

              {/* Sub-group rows */}
              {isExpanded &&
                subGroups.map(({ info, locked }) => {
                  const isSecurityLocked = SECURITY_LOCKED_SUB_GROUPS.has(info.key);

                  return (
                    <div
                      key={info.key}
                      className={`flex items-center justify-between px-4 py-3 border-b border-theme-border last:border-b-0 ${locked ? 'cursor-pointer hover:bg-theme-surface-hover/50 transition-colors' : ''}`}
                      onClick={locked ? () => setUpgradeFeature(info.feature ?? null) : undefined}
                      role={locked ? 'button' : undefined}
                    >
                      <div className="flex-1 mr-4 min-w-0">
                        <div
                          className={`flex items-center gap-1.5 text-sm font-medium ${locked ? 'text-theme-text-tertiary' : 'text-theme-text'}`}
                        >
                          {(locked || isSecurityLocked) && <Lock className="w-3 h-3 shrink-0" />}
                          {info.label}
                          {locked && (
                            <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-theme-primary/20 text-theme-primary">
                              PREMIUM
                            </span>
                          )}
                          {isSecurityLocked && !locked && (
                            <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-500/20 text-amber-500">
                              REQUIRED
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-theme-text-tertiary mt-0.5">
                          {info.description}
                          {isSecurityLocked && (
                            <span className="ml-1 text-amber-500/80">
                              — cannot be disabled for your security
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {CHANNEL_LABELS.map((channel) => (
                          <MiniToggle
                            key={channel.key}
                            enabled={
                              isSecurityLocked || (!locked && currentPrefs[info.key][channel.key])
                            }
                            label={channel.label}
                            onToggle={() => handleChannelToggle(info.key, channel.key, locked)}
                            disabled={locked || (channel.key === 'webPush' && webPush.isLoading)}
                            securityLocked={isSecurityLocked}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}

              {isExpanded && category === 'monitoring' && canChooseSlateBooks && (
                <div className="px-4 py-3 border-b border-theme-border last:border-b-0">
                  <div className="text-sm font-medium text-theme-text">Slate alert books</div>
                  <div className="text-xs text-theme-text-tertiary mt-0.5">
                    Big line moves at these books send slate alerts, one per book.
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {SPORTSBOOKS_ODDS_API.map((book) => (
                      <MiniToggle
                        key={book}
                        enabled={slateBooks.includes(book)}
                        label={getBookDisplayName(book)}
                        onToggle={() => toggleSlateBook(book)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {upgradeFeature && (
        <UpgradeModal
          mode="feature"
          feature={upgradeFeature}
          onDismiss={() => setUpgradeFeature(null)}
        />
      )}
    </section>
  );
}
